import { TALK_MENTION_PLACEHOLDER } from "~/consts/TalkConfig";
import { ContractMember } from "~/entities/ContractMember";
import type { PlanInfo } from "~/entities/PlanInfo";
import { newDateTalk, type Talk } from "~/entities/Talk";
import { TalkRoom } from "~/entities/TalkRoom";
import type { TalkRoomUser } from "~/entities/TalkRoomUser";

// 閲覧権限
export const canViewTalkRoom = (talkRoom: TalkRoom | undefined, isOwner: boolean, isMember: boolean) => {
  if (isOwner) return true;
  if (!talkRoom) return false;
  if (talkRoom.published) {
    if (talkRoom.isFree) {
      return true;
    } else {
      return isMember;
    }
  } else {
    return false;
  }
};

// トークルームアイコン
export const getTalkRoomLabel = (talkRoom: TalkRoom, planInfo: PlanInfo) => {
  return talkRoom.isDirectMessage ? dMTalkRoomLabel(talkRoom, planInfo) : talkRoom.roomName;
};

// トークルームアイコン
export const getTalkRoomIcon = (talkRoom: TalkRoom, planInfo: PlanInfo) => {
  return talkRoom.isDirectMessage ? dMTalkRoomIcon(talkRoom, planInfo) : groupTalkRoomIcon(talkRoom, planInfo);
};

export function isContractExpired(contractMember: ContractMember): boolean {
  // クーリング・オフの場合は即時契約終了とみなす
  if (contractMember.coolingOffDate) {
    return true;
  }
  // 契約終了日が過去の場合は契約終了とみなす
  return contractMember.endDate < new Date().toISOString().split("T")[0];
}

function groupTalkRoomIcon(talkRoom: TalkRoom, planInfo: PlanInfo) {
  if (canViewTalkRoom(talkRoom, planInfo.isOwner, isContractPlan(planInfo))) {
    if (talkRoom.isOnlyOwnerPost) {
      return "assets/images/icon/talkroom-owner.svg";
    }
    return talkRoom.isFree ? "assets/images/icon/talkroom-public.svg" : "assets/images/icon/talkroom-private.svg";
  }
  if (talkRoom.isOnlyOwnerPost) {
    return "assets/images/icon/talkroom-owner-off.svg";
  }
  return talkRoom.isFree ? "assets/images/icon/talkroom-public-off.svg" : "assets/images/icon/talkroom-private-off.svg";
}

function dMTalkRoomIcon(talkRoom: TalkRoom, planInfo: PlanInfo) {
  if (planInfo.isOwner) {
    return talkRoom.dmUser.userImage;
  }
  // dmの場合は、AtomsUserIconを使うので、generateImgPathを通して返す
  return generateImgPath("assets/images/icon/talkroom-owner.svg");
}

export function dMTalkRoomLabel(talkRoom: TalkRoom, planInfo: PlanInfo) {
  if (planInfo.isOwner) {
    return talkRoom.dmUser.userName;
  }
  return "講師＆運営";
}

export function dMTalkRoomDescription() {
  return "講師＆運営とのダイレクトメッセージ";
}

export function canSearchTalk(keyword: string) {
  // 2 文字以上または漢字 1 文字以上
  // ref: https://zenn.dev/raster/articles/a85e115b2ae7e5#%E3%81%93%E3%82%8C%E3%81%A7%E6%B1%BA%E3%81%BE%E3%82%8A%EF%BC%81%E6%BC%A2%E5%AD%97%E3%81%AE%E6%AD%A3%E8%A6%8F%E8%A1%A8%E7%8F%BE
  return keyword.length >= 2 || (keyword.length === 1 && /\p{sc=Han}/u.test(keyword));
}

export const decorateByMention = (
  src: string,
  mentionUserIds: string[] | undefined,
  talkRoomUsers: { [id: string]: TalkRoomUser },
  color: string = "rgb(17, 156, 246)",
) => {
  if (!mentionUserIds) {
    return src;
  }
  // メンション表示
  let text = src;
  for (const mentionUserId of mentionUserIds) {
    const mentionUser = talkRoomUsers[mentionUserId];
    const userName = mentionUser ? mentionUser.userName : "(unknown)";
    text = text.replace(TALK_MENTION_PLACEHOLDER, ` <span style="color: ${color}">@${userName}</span> `);
  }
  return text;
};

export const insertDatesToTalks = (activeTalks: Talk[], noPreviousTalk: boolean, isOwnerOrMember: boolean) => {
  const newTalks: Talk[] = [];
  let beforeTalk: Talk | null = null;

  for (let i = 0; i < activeTalks.length; i++) {
    const talk = activeTalks[i];
    // 最初のトークor前のトークと異なれば日付を挿入
    if ((i === 0 && noPreviousTalk) || (beforeTalk && talk.postDateTime && !beforeTalk.isSameDate(talk))) {
      newTalks.push(newDateTalk(talk.postDateTime!));
    }

    // 画像URLを調整
    const adjustImageUrl = (talk: Talk) => {
      if (talk.messageType !== "image") {
        return;
      }
      if (isOwnerOrMember) {
        // コンテンツURLを S3署名付きURL から CFn URL に変換する
        // TODO 講師または入会済みの場合のみ変換する
        talk.contentsUrl = ConvertS3SignedUrlToCFnUrl(talk.contentsUrl);
      }

      if (talk.thumbnailImageUrl && isOwnerOrMember) {
        // サムネイルURLがある画像は S3署名付きURL から CFn URL に変換する
        // TODO 講師または入会済みの場合のみ変換する
        talk.thumbnailImageUrl = ConvertS3SignedUrlToCFnUrl(talk.thumbnailImageUrl);
      } else if (talk.thumbnailImage) {
        // thumbnailImageUrl がない旧形式データの場合は、thumbnailImage に設定されている base64 画像を表示
        talk.thumbnailImageUrl = talk.thumbnailImage;
      }
      // メモリ使用量削減のためbase64データは削除しておく
      talk.thumbnailImage = "";
    };
    // トークに対して調整
    adjustImageUrl(talk);
    // 返信トークもあれば調整
    if (talk.replyTalk) {
      adjustImageUrl(talk.replyTalk);
    }
    // スレッドトークもあれば調整
    if (talk.threadTalk) {
      adjustImageUrl(talk.threadTalk);
    }

    newTalks.push(talk);
    beforeTalk = talk;
  }
  return newTalks;
};

declare module "@vue/runtime-core" {
  interface ComponentCustomProperties {
    canViewTalkRoom: (talkRoom: TalkRoom | undefined, isOwner: boolean, isMember: boolean) => boolean;
    getTalkRoomIcon: (talkRoom: TalkRoom, planInfo: PlanInfo) => string;
    dMTalkRoomLabel: (talkRoom: TalkRoom, planInfo: PlanInfo) => string;
    dMTalkRoomDescription: () => string;
    canSearchTalk: (keyword: string) => boolean;
    decorateByMention: (
      src: string,
      mentionUserIds: string[] | undefined,
      talkRoomUsers: { [id: string]: TalkRoomUser },
      color?: string,
    ) => string;
  }
}
